/**
 * ── Entitlement Access Hook ──
 * Unified access resolution that combines the module system, capability system,
 * tier system, and commercial catalog into a single query point.
 *
 * This is the primary hook for determining access to any module, feature,
 * or capability in MemberOS. It supports:
 * - Plan-based access
 * - Module gating (3-tier cascade)
 * - Capability gating
 * - Add-on detection
 * - Trial/bonus/grandfathered states
 * - UI behavior (show/tease/lock/hide)
 */

import { useCallback, useMemo } from "react";
import { useModules, type ModuleStatus } from "@/hooks/useModules";
import { useTier } from "@/contexts/TierContext";
import {
  type AccessState,
  type GateBehavior,
  getGateBehavior,
  getAddOnsForModule,
  getAddOnsForCapability,
  getMinPlanForModule,
  getMinPlanForCapability,
  type AddOnDefinition,
  type PlanDefinition,
} from "@/lib/commercial-catalog";

export interface EntitlementResult {
  /** Whether the user can access this right now */
  hasAccess: boolean;
  /** The commercial access state */
  accessState: AccessState;
  /** UI behavior directive */
  behavior: GateBehavior;
  /** The effective module status from the cascade */
  moduleStatus: ModuleStatus | null;
  /** Where access is restricted, if applicable */
  restrictedBy: "platform" | "association" | "org" | null;
  /** Applicable add-ons for upgrade */
  availableAddOns: AddOnDefinition[];
  /** The minimum plan that includes this */
  minPlan: PlanDefinition | undefined;
  /** Whether this is a temporary access (trial/bonus) */
  isTemporary: boolean;
}

export function useEntitlementAccess() {
  const { hasModule, hasCapability, getModule, getCapability, modules } = useModules();
  const { hasFeature } = useTier();

  /**
   * Check access for a module key.
   */
  const checkModule = useCallback(
    (moduleKey: string): EntitlementResult => {
      const mod = getModule(moduleKey);
      const effectiveStatus = mod?.effective_status ?? "inactive";
      const isActive = effectiveStatus === "active";
      const isTrial = effectiveStatus === "trial";
      const isComingSoon = effectiveStatus === "coming_soon";

      let accessState: AccessState;
      if (isActive) {
        accessState = "included";
      } else if (isTrial) {
        accessState = "trial";
      } else if (isComingSoon || mod?.restricted_by === "platform") {
        accessState = "coming_soon";
      } else {
        // Check if there's an add-on available
        const addOns = getAddOnsForModule(moduleKey);
        accessState = addOns.length > 0 ? "addon" : "locked";
      }

      return {
        hasAccess: isActive || isTrial,
        accessState,
        behavior: getGateBehavior(accessState),
        moduleStatus: effectiveStatus,
        restrictedBy: mod?.restricted_by ?? null,
        availableAddOns: getAddOnsForModule(moduleKey),
        minPlan: getMinPlanForModule(moduleKey),
        isTemporary: isTrial,
      };
    },
    [getModule],
  );

  /**
   * Check access for a capability key.
   */
  const checkCapability = useCallback(
    (capabilityKey: string): EntitlementResult => {
      const cap = getCapability(capabilityKey);
      const effectiveStatus = cap?.effective_status ?? "inactive";
      const isActive = effectiveStatus === "active";
      const isTrial = effectiveStatus === "trial";

      let accessState: AccessState;
      if (isActive) {
        accessState = "included";
      } else if (isTrial) {
        accessState = "trial";
      } else {
        const addOns = getAddOnsForCapability(capabilityKey);
        accessState = addOns.length > 0 ? "addon" : "locked";
      }

      return {
        hasAccess: isActive || isTrial,
        accessState,
        behavior: getGateBehavior(accessState),
        moduleStatus: effectiveStatus,
        restrictedBy: cap?.restricted_by ?? null,
        availableAddOns: getAddOnsForCapability(capabilityKey),
        minPlan: getMinPlanForCapability(capabilityKey),
        isTemporary: isTrial,
      };
    },
    [getCapability],
  );

  /**
   * Unified check — accepts module key OR capability key.
   * Convention: capability keys contain a dot (e.g., "events.surveys"),
   * module keys do not (e.g., "events").
   */
  const check = useCallback(
    (key: string): EntitlementResult => {
      return key.includes(".") ? checkCapability(key) : checkModule(key);
    },
    [checkModule, checkCapability],
  );

  /**
   * Quick boolean check — does the user have access?
   */
  const can = useCallback((key: string): boolean => check(key).hasAccess, [check]);

  /**
   * Quick behavior check — what should the UI do?
   */
  const getBehavior = useCallback((key: string): GateBehavior => check(key).behavior, [check]);

  /**
   * Filter a list of items by entitlement, applying behavior rules.
   * Items with "hide" behavior are removed; items with other behaviors are kept
   * with their entitlement result attached.
   */
  const filterByAccess = useCallback(
    <T extends { module?: string; capability?: string }>(items: T[]): Array<T & { entitlement: EntitlementResult }> => {
      return items
        .map((item) => {
          const key = item.capability || item.module;
          if (!key)
            return {
              ...item,
              entitlement: {
                hasAccess: true,
                accessState: "included" as const,
                behavior: "show" as const,
                moduleStatus: null,
                restrictedBy: null,
                availableAddOns: [],
                minPlan: undefined,
                isTemporary: false,
              },
            };
          const entitlement = check(key);
          return { ...item, entitlement };
        })
        .filter((item) => item.entitlement.behavior !== "hide");
    },
    [check],
  );

  return useMemo(
    () => ({
      check,
      checkModule,
      checkCapability,
      can,
      getBehavior,
      filterByAccess,
      // Passthrough from existing hooks for backward compat
      hasModule,
      hasCapability,
      hasFeature,
      modules,
    }),
    [
      check,
      checkModule,
      checkCapability,
      can,
      getBehavior,
      filterByAccess,
      hasModule,
      hasCapability,
      hasFeature,
      modules,
    ],
  );
}
